// src/components/ConfigScreen.jsx
// Configurações premium.
// Rota: /settings
// Rotas usadas:
// /profile
// /security
// /plan-management
// /integrations
// /help
// UI isolada em .cfg-* para não mexer em .dash-* global.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronRight,
  CircleHelp,
  CreditCard,
  Home,
  Link2,
  LogOut,
  Menu,
  Settings2,
  ShieldCheck,
  Sparkles,
  UserRound,
} from "lucide-react";

import AppSidebar from "./AppSidebar";
import Logo from "../assets/logoClaro.webp";
import "../sass/pages/config.scss";

const AUTH_KEYS = {
  USER_NAME: "psy_usuario",
  PLAN: "plano",
};

function readStorage(keys = []) {
  for (const key of keys) {
    const value = sessionStorage.getItem(key) || localStorage.getItem(key);

    if (value && String(value).trim() !== "") {
      return value;
    }
  }

  return "";
}

function getFirstName(name = "") {
  const clean = String(name || "").trim();

  if (!clean) return "";

  return clean.split(/\s+/)[0];
}

function normalizePlan(value = "") {
  const plan = String(value || "").trim();

  if (!plan) return "Plano não informado";

  return plan.charAt(0).toUpperCase() + plan.slice(1).toLowerCase();
}

function clearAuthStorage() {
  const keys = [
    "psy_token",
    "psy_id",
    "psy_usuario",
    "psy_email",
    "psy_nome",
    "plano",
    "token",
    "id",
    "user",
    "usuario",
    "email",
  ];

  keys.forEach((key) => {
    try {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    } catch {
      // storage indisponível
    }
  });
}

function ConfigOption({
  icon: Icon,
  title,
  subtitle,
  badge,
  onClick,
  danger = false,
  soon = false,
}) {
  return (
    <button
      className={`cfg-item ${danger ? "cfg-item--danger" : ""} ${
        soon ? "cfg-item--soon" : ""
      }`}
      type="button"
      onClick={onClick}
      title={title}
      disabled={soon}
    >
      <div className="cfg-item__left">
        <div className="cfg-ico" aria-hidden="true">
          <Icon size={20} />
        </div>

        <div className="cfg-item__txt">
          <div className="cfg-title">{title}</div>
          {subtitle ? <div className="cfg-subtitle">{subtitle}</div> : null}
        </div>
      </div>

      <div className="cfg-item__right">
        {badge ? <span className="cfg-badge">{badge}</span> : null}
        <span className="cfg-chev" aria-hidden="true">
          <ChevronRight size={16} />
        </span>
      </div>
    </button>
  );
}

export default function ConfigScreen() {
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const nomeUsuario = useMemo(
    () => readStorage([AUTH_KEYS.USER_NAME]) || "Usuário",
    []
  );

  const firstName = useMemo(() => getFirstName(nomeUsuario), [nomeUsuario]);

  const plan = useMemo(
    () => normalizePlan(readStorage([AUTH_KEYS.PLAN])),
    []
  );

  useEffect(() => {
    if (!confirmOpen) return undefined;

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setConfirmOpen(false);
      }
    }

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [confirmOpen]);

  const go = (path) => navigate(path);

  const logout = () => {
    clearAuthStorage();
    navigate("/login", { replace: true });
  };

  return (
    <div className="cfg-page">
      <div className="cfg-bg" aria-hidden="true">
        <span className="cfg-bg__orb cfg-bg__orb--one" />
        <span className="cfg-bg__orb cfg-bg__orb--two" />
        <span className="cfg-bg__orb cfg-bg__orb--three" />
      </div>

      <AppSidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <header className="cfg-topbar">
        <button
          className="cfg-icon-btn"
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir menu"
        >
          <Menu size={19} aria-hidden="true" />
        </button>

        <div className="cfg-brand">
          <img src={Logo} alt="PsyAssist" className="cfg-brand__logo" />

          <div className="cfg-brand__text">
            <strong>Configurações</strong>
            <span>Conta • {nomeUsuario}</span>
          </div>
        </div>

        <div className="cfg-top-actions">
          <button
            className="cfg-btn cfg-btn--ghost cfg-btn--desktop"
            type="button"
            onClick={() => go("/")}
          >
            <Home size={16} />
            Início
          </button>

          <button
            className="cfg-btn cfg-btn--primary"
            type="button"
            onClick={() => go("/profile")}
          >
            <UserRound size={16} />
            Perfil
          </button>
        </div>
      </header>

      <main className="cfg-main">
        <section className="cfg-hero">
          <div className="cfg-hero__content">
            <span className="cfg-kicker">
              <Settings2 size={14} aria-hidden="true" />
              Central da conta
            </span>

            <h1>Configurações</h1>

            <p>
              {firstName ? `${firstName}, ` : ""}
              gerencie dados da conta, segurança, integrações, assinatura e
              canais de suporte em um único lugar.
            </p>

            <div className="cfg-hero__actions">
              <button
                className="cfg-btn cfg-btn--hero-primary"
                type="button"
                onClick={() => go("/profile")}
              >
                <UserRound size={16} />
                Editar perfil
              </button>

              <button
                className="cfg-btn cfg-btn--hero-ghost"
                type="button"
                onClick={() => go("/security")}
              >
                <ShieldCheck size={16} />
                Segurança
              </button>
            </div>
          </div>

          <aside className="cfg-hero-card">
            <div className="cfg-hero-card__icon" aria-hidden="true">
              <Sparkles size={22} />
            </div>

            <div>
              <small>Plano atual</small>
              <strong>{plan}</strong>
              <span>
                Consulte assinatura e recursos disponíveis na gestão do plano.
              </span>
            </div>
          </aside>
        </section>

        <section className="cfg-grid">
          <article className="cfg-panel">
            <div className="cfg-panel__head">
              <div>
                <span>Conta</span>
                <h2>Dados e acesso</h2>
                <p>Configurações principais da sua conta.</p>
              </div>
            </div>

            <div className="cfg-list">
              <ConfigOption
                icon={UserRound}
                title="Perfil"
                subtitle="Nome, dados profissionais e informações da conta"
                badge="Conta"
                onClick={() => go("/profile")}
              />

              <ConfigOption
                icon={ShieldCheck}
                title="Segurança"
                subtitle="Senha, sessão e proteção de acesso"
                badge="Proteção"
                onClick={() => go("/security")}
              />
            </div>
          </article>

          <article className="cfg-panel">
            <div className="cfg-panel__head">
              <div>
                <span>Conexões</span>
                <h2>Integrações</h2>
                <p>Conecte serviços externos à sua rotina no PsyAssist.</p>
              </div>
            </div>

            <div className="cfg-list">
              <ConfigOption
                icon={Link2}
                title="Integrações"
                subtitle="Google Agenda e futuras conexões da plataforma"
                badge="Google"
                onClick={() => go("/integrations")}
              />
            </div>
          </article>

          <article className="cfg-panel">
            <div className="cfg-panel__head">
              <div>
                <span>Assinatura</span>
                <h2>Plano e suporte</h2>
                <p>Gerencie assinatura e canais de atendimento.</p>
              </div>
            </div>

            <div className="cfg-list">
              <ConfigOption
                icon={CreditCard}
                title="Gestão do Plano"
                subtitle="Assinatura, recursos disponíveis e alteração de plano"
                badge={plan}
                onClick={() => go("/plan-management")}
              />

              <ConfigOption
                icon={CircleHelp}
                title="Ajuda e Suporte"
                subtitle="Central de ajuda, contato e dúvidas frequentes"
                badge="Suporte"
                onClick={() => go("/help")}
              />
            </div>
          </article>
        </section>

        <section className="cfg-panel cfg-panel--danger">
          <div className="cfg-panel__head">
            <div>
              <span>Sessão</span>
              <h2>Acesso atual</h2>
              <p>Finalize a sessão neste dispositivo com segurança.</p>
            </div>
          </div>

          <ConfigOption
            icon={LogOut}
            title="Sair da conta"
            subtitle="Remove a sessão atual e retorna para o login"
            badge="Logout"
            onClick={() => setConfirmOpen(true)}
            danger
          />
        </section>
      </main>

      {confirmOpen ? (
        <div
          className="cfg-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cfg-dialog-title"
          onClick={() => setConfirmOpen(false)}
        >
          <div
            className="cfg-dialog"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="cfg-dialog__icon" aria-hidden="true">
              <LogOut size={24} />
            </div>

            <div id="cfg-dialog-title" className="cfg-dialog__title">
              Sair do aplicativo?
            </div>

            <div className="cfg-dialog__desc">
              Você será desconectado desta sessão. Para acessar novamente, será
              necessário fazer login.
            </div>

            <div className="cfg-dialog__actions">
              <button
                className="cfg-btn cfg-btn--ghost-dark"
                type="button"
                onClick={() => setConfirmOpen(false)}
              >
                Cancelar
              </button>

              <button
                className="cfg-btn cfg-btn--danger"
                type="button"
                onClick={logout}
              >
                <LogOut size={16} />
                Sair
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
